import type {
  Attachment,
  AutoFeedbackItemTransformed,
  InteractionStepType,
  PromptInputStepResponse,
} from '@scale/shared/types/chat';
import {
  AutoFeedbackItemStatus,
  ModelResponseEditorStepResponse,
  AutoFeedbackRequest,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared/defaults';
import { useQuery } from 'react-query';
import {
  fetchFeedback,
  getStepCompatibility,
  joinFeedback,
  validateAutoFeedbackStepType,
} from '../helpers';
import { useContext, useRef, useCallback, useEffect } from 'react';
import {
  useChatStoreRef,
  useStepResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useTemplateVariableStore } from 'react-scale/components/scalers/tasks/textcollection/stores/TemplateVariableStore';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useAutoFeedbackStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/stores/useAutoFeedbackStore';
import _ from 'lodash';
import SAlert from 'react-s-alert';
import { EvaluatorTemplateJSON } from '@scale/shared/types/corp/chatCompanion';

export interface UseAutoFeedbackProps {
  id: string;
  responseIndex?: number;
  reviewLevel: ReviewLevel;
  stepType: InteractionStepType;
  stepId?: string;
  // TODO: Migrate to IEvaluatorTemplateProps
  evaluatorOverrides?: any[];
  multichatIndex?: number;
  template?: EvaluatorTemplateJSON;
}

export interface HandleFetchFeedbackInput {
  inputText: string;
  attachments?: Attachment[];
  template?: EvaluatorTemplateJSON;
}

export interface UseAutoFeedbackResponse {
  feedback?: AutoFeedbackItemTransformed[];
  compatibleSteps: (InteractionStepType | string)[]; // step id or step type
  isFetchingFeedback: boolean;
  isFetchingStepCompatible: boolean;
  error: boolean;
  handleFetchFeedback: (input: HandleFetchFeedbackInput) => Promise<void>;
  setFeedbackState: (feedbackId: string, state: AutoFeedbackItemStatus) => void;
}

const useAutoFeedback = ({
  id,
  responseIndex,
  reviewLevel,
  stepType,
  stepId,
  evaluatorOverrides,
  multichatIndex,
  template,
}: UseAutoFeedbackProps): UseAutoFeedbackResponse | null => {
  const chatStoreRef = useChatStoreRef();

  const response = useStepResponse<ModelResponseEditorStepResponse | PromptInputStepResponse>(
    responseIndex ?? 0,
  );
  const templateVariables = useTemplateVariableStore();
  const taskContext = useContext(ChatTaskContext);
  const inputContent = useRef<HandleFetchFeedbackInput>({
    inputText: '',
    attachments: undefined,
    template,
  });

  const key = `fetchFeedback-${id}${multichatIndex !== undefined ? `-${multichatIndex}` : ''}`;

  const { getAutoFeedbackState, setAutoFeedbackState } = useAutoFeedbackStore();
  const [allFeedback, setAllFeedback] = [
    getAutoFeedbackState(key)?.feedback,
    (feedback?: AutoFeedbackItemTransformed[]) => setAutoFeedbackState(key, { feedback }),
  ];

  useEffect(() => {
    if (!allFeedback && response?.autoFeedbackResponse?.feedback) {
      setAllFeedback(response.autoFeedbackResponse.feedback);
    }
  }, [allFeedback, response?.autoFeedbackResponse?.feedback, setAllFeedback]);

  const disagreedFeedback = allFeedback?.filter(f => f.status === AutoFeedbackItemStatus.IGNORED);

  const {
    isFetching: isFetchingFeedback,
    error: fetchFeedbackError,
    refetch: handleFetchFeedback,
  } = useQuery([key], {
    queryFn: async () => {
      const currentChatStoreState = chatStoreRef.current.getState();
      const responses = currentChatStoreState.responses;
      const params = currentChatStoreState.config;

      let newFeedback: AutoFeedbackItemTransformed[] = [];

      validateAutoFeedbackStepType(stepType);

      const payload: AutoFeedbackRequest = {
        assignmentId: taskContext.assignmentId ?? taskContext.taskId,
        projectId: taskContext.projectId,
        stepType,
        stepId,
        responses,
        params,
        reviewLevel,
        mostRecentFeedback: allFeedback ?? [],
        templateVariables,
        stream: true,
        attachments: inputContent.current.attachments,
        inputContent: inputContent.current.inputText,
        responseIndex,
        evaluatorOverrides,
        template,
      };

      // Stream feedback by category
      await fetchFeedback({
        payload,
        onOpen: async response => {
          if (response.status !== 200) {
            const body = await response.json();
            const error = JSON.stringify(body);
            SAlert.error(error);
            throw new Error(error);
          }
        },
        onMessage: event => {
          const feedbackItem = JSON.parse(event.data) as AutoFeedbackItemTransformed;
          newFeedback = [...newFeedback, feedbackItem];
        },
        onError: error => {
          SAlert.error(JSON.stringify(error));
          throw new Error(error);
        },
      });

      const oldFeedback = _.cloneDeep(allFeedback);
      return joinFeedback(oldFeedback, newFeedback ?? []) ?? [];
    },
    enabled: false,
    retry: false,
    onSuccess: data => {
      setAllFeedback(data);
    },
  });

  const modifyFeedbackState = useCallback(
    (feedbackId: string, state: AutoFeedbackItemStatus) => {
      const feedback = allFeedback?.find(f => f.id === feedbackId);
      if (!feedback) {
        return;
      }
      feedback.status = state;
      setAllFeedback([...(allFeedback ?? [])]);
    },
    [allFeedback, setAllFeedback],
  );

  const getFeedback = useCallback(
    async (input: HandleFetchFeedbackInput) => {
      setAllFeedback(disagreedFeedback);
      inputContent.current = input;
      await handleFetchFeedback();
    },
    [disagreedFeedback, handleFetchFeedback, setAllFeedback],
  );

  const {
    data: compatibleSteps,
    isFetching: isFetchingStepCompatible,
    error: stepCompatibleError,
  } = useQuery(
    ['stepCompatibility', taskContext.projectId, reviewLevel],
    async () => {
      if (!taskContext.projectId || reviewLevel === null) {
        return [];
      }

      const res = await getStepCompatibility({
        projectId: taskContext.projectId,
        reviewLevel,
      });

      return res.compatibleSteps;
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    },
  );

  return {
    feedback: allFeedback,
    compatibleSteps: compatibleSteps ?? [],
    isFetchingFeedback,
    isFetchingStepCompatible,
    error: !!(fetchFeedbackError || stepCompatibleError),
    handleFetchFeedback: getFeedback,
    setFeedbackState: modifyFeedbackState,
  };
};

export default useAutoFeedback;
